/**
 *
 * @module eventSchemataProvider
 *
 */

import Check from 'check-types';
import Services from './../services/entry';
import UserActivityEvent from './userActivityEvent';
import EventSchema from './eventSchema';
import EventSchemata from './eventSchemata';

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    nonEmptyObject,
    instanceStrict,
    boolean,
    array
} = Check.assert;

/**
 *
 * @class EventSchemataProvider
 * @desc The schema provider that should be used to generate event payloads
 *
 */
export default class EventSchemataProvider {

    constructor() {

        /**
         *
         * @access private
         * @type {Object<String, SDK.UserActivity.EventSchemata>|Object}
         * @note the spec notes that the key should be `SDK.UserActivity.UserActivityEvent` but JS does not have the
         * ability to use an `Object` as a key so we use `SDK.UserActivity.UserActivityEvent.eventUrn` instead
         *
         */
        this.schemata = {};

    }

    /**
     *
     * @access public
     * @param {Object} options
     * @param {SDK.UserActivity.UserActivityEvent} options.event
     * @param {Array<SDK.UserActivity.EventSchema>} options.schemata
     * @param {Boolean} options.passthrough
     * @desc duplicate keys are overwritten
     * @note `schemata` is renamed `schemaArray` since we already have a `schemata` property on the constructor
     *
     */
    setSchemata(options) {

        const errorMessage = {
            method: 'EventSchemataProvider.setSchemata(options)',
            get options() {
                return `${this.ctor} options should be a non-empty Object`;
            },
            get event() {
                return `${this.method} event should be an instance of SDK.UserActivity.UserActivityEvent`;
            },
            get schemata() {
                return `${this.method} schemata should be an Array of SDK.UserActivity.EventSchema`;
            },
            get passthrough() {
                return `${this.method} passthrough should be a Boolean`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { schemata } = this;
        const { event, schemata: schemaArray, passthrough } = options;

        instanceStrict(event, UserActivityEvent, errorMessage.event, InvalidArgumentException);
        array.of.instanceStrict(schemaArray, EventSchema, errorMessage.schemata, InvalidArgumentException);
        boolean(passthrough, errorMessage.passthrough, InvalidArgumentException);

        schemata[event.eventUrn] = new EventSchemata(schemaArray, passthrough);

    }

    /**
     *
     * @access private
     * @param {SDK.UserActivity.UserActivityEvent} event
     * @note if no schema is found we return a bare bones version of `SDK.UserActivity.EventSchemata` to ensure our
     * payload generator functions correctly
     * @note we are unable to use the `SDK.UserActivity.UserActivityEvent` as the key so we do the lookup via the
     * `eventUrn` property
     * @note if a schemata isn't found we create a new one using default values of `schema=[], passthrough=true`
     * @returns {SDK.UserActivity.EventSchemata}
     *
     */
    getSchemata(event) {

        const errorMessage = {
            method: 'EventSchemataProvider.getSchemata(event)',
            get event() {
                return `${this.method} event should be an instance of SDK.UserActivity.UserActivityEvent`;
            }
        };

        const { schemata } = this;

        instanceStrict(event, UserActivityEvent, errorMessage.event, InvalidArgumentException);

        return schemata[event.eventUrn] || new EventSchemata([], true);

    }

}



// WEBPACK FOOTER //
// ./src/userActivity/eventSchemataProvider.js